import { supabase } from './supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

async function getHeaders(orgId) {
  let token = null;

  // Try getSession
  try {
    const { data } = await supabase.auth.getSession();
    token = data?.session?.access_token;
  } catch (e) {
    // ignore
  }

  // Fallback: read from all possible localStorage keys
  if (!token) {
    for (const key of Object.keys(localStorage)) {
      if (key.includes('auth-token') || key.includes('pi-portal-auth')) {
        try {
          const parsed = JSON.parse(localStorage.getItem(key));
          // Supabase stores: { access_token, ... } or { session: { access_token } }
          token = parsed?.access_token || parsed?.session?.access_token;
          if (token) break;
        } catch (e) {
          // ignore
        }
      }
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };
  // Only set org header when we actually have a real UUID — avoids sending
  // the literal string "undefined" on first render before OrgContext loads.
  if (orgId && typeof orgId === 'string' && orgId !== 'undefined') {
    headers['X-Org-Id'] = orgId;
  }
  return headers;
}

// One silent attempt to renew an expired session. Null when it can't —
// the refresh token is gone or revoked, so a re-login is required.
async function refreshedToken() {
  try {
    const { data } = await supabase.auth.refreshSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
}

// Global expired-session handling: when a signed-in area is left holding a
// dead session (laptop slept past the refresh window, tab suspended), no
// screen should ever surface a raw auth error — least of all a video
// visit. Bounce to sign-in with a friendly note instead. Tokenized guest
// pages (/visit/…) are untouched: they never had an auth session.
function bounceToLogin() {
  const p = window.location.pathname;
  if (p.startsWith('/app') || p.startsWith('/client')) {
    // Carry the page along so sign-in lands back on it (a referral opened
    // from a text keeps its ?open=… this way).
    const from = encodeURIComponent(p + window.location.search);
    window.location.assign(`/login?expired=1&from=${from}`);
    return true;
  }
  return false;
}

async function request(path, opts = {}, _retried = false) {
  const { method = 'GET', body, orgId } = opts;
  const headers = await getHeaders(orgId);
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json();
  if (!res.ok) {
    // Expired session: refresh once and replay the request transparently.
    // Only if the refresh itself fails do we send the user to sign in.
    if (res.status === 401 && !_retried && !path.startsWith('/guest-visit') && !path.startsWith('/book/')) {
      const renewed = await refreshedToken();
      if (renewed) return request(path, opts, true);
      bounceToLogin();
    }
    const err = new Error(data.error || `Request failed: ${res.status}`);
    err.status = res.status; // lets callers special-case 401 (expired session)
    err.code = data.code || null; // e.g. 'trial_ended_add_card' on a 402
    throw err;
  }
  return data;
}

export const api = {
  // Appointments
  getAppointments: (orgId, params = {}) => {
    const qs = new URLSearchParams({ include_collaborator: 'true', ...params }).toString();
    return request(`/appointments${qs ? '?' + qs : ''}`, { orgId });
  },
  getAppointment: (orgId, id) => request(`/appointments/${id}`, { orgId }),
  getAvailability: (orgId, params) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/appointments/availability?${qs}`, { orgId });
  },
  createAppointment: (orgId, data) => request('/appointments', { method: 'POST', body: data, orgId }),
  updateAppointment: (orgId, id, data) => request(`/appointments/${id}`, { method: 'PATCH', body: data, orgId }),
  deleteAppointment: (orgId, id) => request(`/appointments/${id}`, { method: 'DELETE', orgId }),

  // Telehealth (video visits)
  getTelehealthLanguages: (orgId) => request('/telehealth/languages', { orgId }),
  getActiveTelehealthSessions: (orgId) => request('/telehealth/active', { orgId }),
  createTelehealthSession: (orgId, data) => request('/telehealth', { method: 'POST', body: data, orgId }),
  getTelehealthSession: (orgId, id) => request(`/telehealth/${id}`, { orgId }),
  joinTelehealthSession: (orgId, id) => request(`/telehealth/${id}/join`, { method: 'POST', orgId }),
  setTelehealthTranscription: (orgId, id, data) => request(`/telehealth/${id}/transcription`, { method: 'POST', body: data, orgId }),
  endTelehealthSession: (orgId, id) => request(`/telehealth/${id}/end`, { method: 'POST', orgId }),
  saveTelehealthUtterances: (orgId, id, utterances) => request(`/telehealth/${id}/utterances`, { method: 'POST', body: { utterances }, orgId }),
  getTelehealthUtterances: (orgId, id) => request(`/telehealth/${id}/utterances`, { orgId }),
  translateCaption: (orgId, data) => request('/telehealth/translate', { method: 'POST', body: data, orgId }),

  addTelehealthGuests: (orgId, id, guests) => request(`/telehealth/${id}/guests`, { method: 'POST', body: { guests }, orgId }),
  geocodeOrg: (data) => request('/directory/geocode', { method: 'POST', body: data }),

  // Referrals v1 — backend is feature-flagged (FEATURE_REFERRALS); these
  // 404 until the flag is enabled on the target environment.
  searchReferralProviders: (orgId, params) => request(`/referrals/search?${new URLSearchParams(params)}`, { orgId }),
  parseReferralQuery: (orgId, text) => request('/referrals/parse', { method: 'POST', body: { text }, orgId }),
  assembleReferralPacket: (orgId, clientId, params = {}) => request(`/referrals/assemble/${clientId}?${new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v)))}`, { orgId }),
  recommendCare: (orgId, clientId) => request('/referrals/recommend', { method: 'POST', body: { client_id: clientId }, orgId }),
  createReferral: (orgId, data) => request('/referrals', { method: 'POST', body: data, orgId }),
  listReferrals: (orgId, box, params = {}) => {
    const qs = new URLSearchParams({ ...(box ? { box } : {}), ...params }).toString();
    return request(`/referrals${qs ? `?${qs}` : ''}`, { orgId });
  },
  bookReferralVisit: (orgId, id, data) => request(`/referrals/${id}/book`, { method: 'POST', body: data, orgId }),
  getReferralBookingOptions: (orgId, id) => request(`/referrals/${id}/booking-options`, { orgId }),
  // Opens the one-page cover sheet PDF in a new tab (authenticated fetch → blob URL).
  openReferralCoverSheet: async (orgId, id) => {
    const headers = await getHeaders(orgId);
    delete headers['Content-Type'];
    const res = await fetch(`${API_URL}/referrals/${id}/cover-sheet`, { headers });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not build the cover sheet');
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
  getReferralAvailability: (orgId, id, params) => request(`/referrals/${id}/availability?${new URLSearchParams(params)}`, { orgId }),
  remindReferralBooking: (orgId, id) => request(`/referrals/${id}/remind`, { method: 'POST', orgId }),
  // Clinic offers (or replaces) up to three first-visit times on an accepted referral (076)
  proposeReferralTimes: (orgId, id, data) => request(`/referrals/${id}/propose`, { method: 'POST', body: data, orgId }),
  getReferralById: (orgId, id) => request(`/referrals/${id}`, { orgId }),
  updateReferralStatus: (orgId, id, status, note, extra = {}) => request(`/referrals/${id}/status`, { method: 'PATCH', body: { status, note, ...extra }, orgId }),
  getReferralSettings: (orgId) => request('/referrals/settings/self', { orgId }),
  updateReferralSettings: (orgId, data) => request('/referrals/settings/self', { method: 'PUT', body: data, orgId }),
  // Cases + coverages (migration 054)
  listCases: (orgId) => request('/cases', { orgId }),
  getCase: (orgId, id) => request(`/cases/${id}`, { orgId }),
  createCase: (orgId, data) => request('/cases', { method: 'POST', body: data, orgId }),
  deleteCase: (orgId, id) => request(`/cases/${id}`, { method: 'DELETE', orgId }),
  listCaseDocuments: (orgId, id) => request(`/cases/${id}/documents`, { orgId }),
  getCaseDocumentUrl: (orgId, id, docId) => request(`/cases/${id}/documents/${docId}/download`, { orgId }),
  updateCase: (orgId, id, data) => request(`/cases/${id}`, { method: 'PATCH', body: data, orgId }),
  addCaseCoverage: (orgId, id, data) => request(`/cases/${id}/coverages`, { method: 'POST', body: data, orgId }),
  updateCaseCoverage: (orgId, id, cid, data) => request(`/cases/${id}/coverages/${cid}`, { method: 'PATCH', body: data, orgId }),
  removeCaseCoverage: (orgId, id, cid) => request(`/cases/${id}/coverages/${cid}`, { method: 'DELETE', orgId }),
  recordCaseEmc: (orgId, id, data) => request(`/cases/${id}/emc`, { method: 'POST', body: data, orgId }),
  getJurisdictions: (orgId) => request('/cases/jurisdictions', { orgId }),
  analyzeCaseIntake: (orgId, data) => request('/cases/intake/analyze', { method: 'POST', body: data, orgId }),
  applyCaseIntake: (orgId, data) => request('/cases/intake/apply', { method: 'POST', body: data, orgId }),
  getMyCareFile: (orgId) => request('/referrals/my-file', { orgId }),
  createMyCareRequest: (orgId, data) => request('/referrals/my-request', { method: 'POST', body: data, orgId }),

  // Guest (visitor) telehealth — token-authenticated public endpoints, no
  // org header or session needed.
  // Quick booking link (no auth; the referral's booking_token is the credential)
  quickBookInfo: (token) => request(`/book/${token}`),
  quickBookAvailability: (token, params) => request(`/book/${token}/availability?${new URLSearchParams(params)}`),
  quickBook: (token, data) => request(`/book/${token}`, { method: 'POST', body: data }),
  quickBookDeclineOffers: (token) => request(`/book/${token}/decline`, { method: 'POST' }),
  getGuestVisit: (token) => request(`/guest-visit/${token}`),
  joinGuestVisit: (token) => request(`/guest-visit/${token}/join`, { method: 'POST' }),
  translateGuestCaption: (token, data) => request(`/guest-visit/${token}/translate`, { method: 'POST', body: data }),
  getTelehealthHistory: (orgId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telehealth/history${qs ? '?' + qs : ''}`, { orgId });
  },
  setTelehealthRecording: (orgId, id, on) => request(`/telehealth/${id}/recording`, { method: 'POST', body: { on }, orgId }),
  getTelehealthRecordingUrl: (orgId, id) => request(`/telehealth/${id}/recording-url`, { orgId }),

  // Appointment Types
  getAppointmentTypes: (orgId) => request('/appointment-types', { orgId }),
  getOrgProviders:     (orgId, locationId) => request(`/appointments/providers${locationId ? `?location_id=${locationId}` : ''}`, { orgId }),
  createAppointmentType: (orgId, data) => request('/appointment-types', { method: 'POST', body: data, orgId }),
  updateAppointmentType: (orgId, id, data) => request(`/appointment-types/${id}`, { method: 'PATCH', body: data, orgId }),
  deleteAppointmentType: (orgId, id) => request(`/appointment-types/${id}`, { method: 'DELETE', orgId }),

  // Directory (cross-tenant, but passes org for partnership annotations)
  getDirectory: (params = {}, orgId) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/directory${qs ? '?' + qs : ''}`, { orgId });
  },

  getDirectoryProviders: (orgId) => request(`/directory/${orgId}/providers`),
  bookAppointment: (data) => request('/book', { method: 'POST', body: data }),

  // Partnerships
  getPartnerships: (orgId) => request('/partnerships', { orgId }),
  getActivePartners: (orgId) => request('/partnerships/active', { orgId }),
  requestPartnership: (orgId, data) => request('/partnerships/request', { method: 'POST', body: data, orgId }),
  acceptPartnership: (orgId, id) => request(`/partnerships/${id}/accept`, { method: 'PATCH', orgId }),
  declinePartnership: (orgId, id) => request(`/partnerships/${id}/decline`, { method: 'PATCH', orgId }),
  revokePartnership: (orgId, id) => request(`/partnerships/${id}`, { method: 'DELETE', orgId }),
  getPartnerStaff: (orgId, partnerId) => request(`/partnerships/${partnerId}/staff`, { orgId }),

  // Appointment Collaborators
  getCollaborators: (orgId, appointmentId) => request(`/appointments/${appointmentId}/collaborators`, { orgId }),
  addCollaborator: (orgId, appointmentId, data) => request(`/appointments/${appointmentId}/collaborators`, { method: 'POST', body: data, orgId }),
  removeCollaborator: (orgId, appointmentId, collabId) => request(`/appointments/${appointmentId}/collaborators/${collabId}`, { method: 'DELETE', orgId }),

  // Form Signing
  uploadFormDocument: async (orgId, file) => {
    const formData = new FormData();
    formData.append('document', file);
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    const res = await fetch(`${API_URL}/forms/upload`, {
      method: 'POST',
      headers: { Authorization: token ? `Bearer ${token}` : '', 'X-Org-Id': orgId },
      body: formData,
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Upload failed');
    return result;
  },
  getFormTemplates: (orgId) => request('/forms/templates', { orgId }),
  createFormTemplate: (orgId, data) => request('/forms/templates', { method: 'POST', body: data, orgId }),
  updateFormTemplate: (orgId, id, data) => request(`/forms/templates/${id}`, { method: 'PATCH', body: data, orgId }),
  deleteFormTemplate: (orgId, id) => request(`/forms/templates/${id}`, { method: 'DELETE', orgId }),
  sendForm: (orgId, data) => request('/forms/send', { method: 'POST', body: data, orgId }),
  getOrgFormInstances: (orgId) => request('/forms/org-instances', { orgId }),
  getMyForms: (orgId) => request('/forms/my-forms', { orgId }),
  getFormForSigning: (orgId, id) => request(`/forms/my-forms/${id}`, { orgId }),
  signForm: (orgId, id, data) => request(`/forms/my-forms/${id}/sign`, { method: 'PATCH', body: data, orgId }),
  getFormAudit: (orgId, id) => request(`/forms/instances/${id}/audit`, { orgId }),

  // Intake Forms
  getIntakeForms: (orgId) => request('/intake-forms', { orgId }),
  createIntakeForm: (orgId, data) => request('/intake-forms', { method: 'POST', body: data, orgId }),
  updateIntakeForm: (orgId, id, data) => request(`/intake-forms/${id}`, { method: 'PATCH', body: data, orgId }),
  deleteIntakeForm: (orgId, id) => request(`/intake-forms/${id}`, { method: 'DELETE', orgId }),
  getIntakeFormClientStatus: (orgId) => request('/intake-forms/client-status', { orgId }),
  getMyIntakeForms: (orgId) => request('/intake-forms/my-forms', { orgId }),
  completeIntakeForm: (orgId, id) => request(`/intake-forms/my-forms/${id}/complete`, { method: 'PATCH', orgId }),

  // Organization
  updateOrg: (orgId, data) => request('/org', { method: 'PATCH', body: data, orgId }),

  // Team
  getOrgMembers: (orgId) => request('/org/members', { orgId }),
  updateOrgMember: (orgId, memberId, data) => request(`/org/members/${memberId}`, { method: 'PATCH', body: data, orgId }),
  removeOrgMember: (orgId, memberId) => request(`/org/members/${memberId}`, { method: 'DELETE', orgId }),
  sendInvitation: (orgId, data) => request('/org/invitations', { method: 'POST', body: data, orgId }),
  getOrgInvitations: (orgId) => request('/org/invitations', { orgId }),
  resendInvitation: (orgId, id) => request(`/org/invitations/${id}/resend`, { method: 'POST', orgId }),
  revokeInvitation: (orgId, id) => request(`/org/invitations/${id}`, { method: 'DELETE', orgId }),

  // Contacts
  getContacts: (orgId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/contacts${qs ? '?' + qs : ''}`, { orgId });
  },
  createContact: (orgId, data) => request('/contacts', { method: 'POST', body: data, orgId }),
  inviteContact: (orgId, id) => request(`/contacts/${id}/invite`, { method: 'POST', orgId }),
  updateContact: (orgId, id, data) => request(`/contacts/${id}`, { method: 'PATCH', body: data, orgId }),
  deleteContact: (orgId, id) => request(`/contacts/${id}`, { method: 'DELETE', orgId }),

  // Notifications
  getNotifications: (orgId) => request('/notifications', { orgId }),

  // Staff
  getStaff: (orgId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/staff${qs ? '?' + qs : ''}`, { orgId });
  },
  // Per-office hours and office assignments for a provider
  // Text-notification numbers (the signed-in person's own)
  // orgId scopes staff numbers to that practice; omit for a patient's own numbers
  listMyNumbers: (orgId) => request('/me/numbers', { orgId }),
  addMyNumber: (body, orgId) => request('/me/numbers', { method: 'POST', body, orgId }),
  resendMyNumberCode: (id, orgId) => request(`/me/numbers/${id}/resend`, { method: 'POST', orgId }),
  confirmMyNumber: (id, code, orgId) => request(`/me/numbers/${id}/confirm`, { method: 'POST', body: { code }, orgId }),
  updateMyNumber: (id, patch, orgId) => request(`/me/numbers/${id}`, { method: 'PATCH', body: patch, orgId }),
  deleteMyNumber: (id, orgId) => request(`/me/numbers/${id}`, { method: 'DELETE', orgId }),
  getStaffAvailability: (orgId, userId) => request(`/staff/${userId}/availability`, { orgId }),
  setStaffAvailability: (orgId, userId, location_id, blocks) => request(`/staff/${userId}/availability`, { method: 'PUT', body: { location_id, blocks }, orgId }),
  getStaffLocations: (orgId, userId) => request(`/staff/${userId}/locations`, { orgId }),
  setStaffLocations: (orgId, userId, location_ids) => request(`/staff/${userId}/locations`, { method: 'PUT', body: { location_ids }, orgId }),

  // Locations
  getLocations:    (orgId)            => request('/locations', { orgId }),
  getLocationScope: (orgId) => request('/locations/scope', { orgId }),
  getLocationHours: (orgId, locationId) => request(`/locations/${locationId}/hours`, { orgId }),
  setLocationHours: (orgId, locationId, blocks) => request(`/locations/${locationId}/hours`, { method: 'PUT', body: { blocks }, orgId }),
  createLocation:  (orgId, data)      => request('/locations', { method: 'POST', body: data, orgId }),
  updateLocation:  (orgId, id, data)  => request(`/locations/${id}`, { method: 'PATCH', body: data, orgId }),
  deleteLocation:  (orgId, id)        => request(`/locations/${id}`, { method: 'DELETE', orgId }),
  bulkCreateLocations: (orgId, locations) => request('/locations/bulk', { method: 'POST', body: { locations }, orgId }),

  // Platform admin
  deletePlatformUser: (id) => request(`/admin/users/${id}`, { method: 'DELETE' }),
  getOrgsOverview: () => request('/admin/organizations-overview'),
  getAdminReferralNetwork: () => request('/admin/referral-network'),
  getPartnershipEdges: () => request('/admin/partnership-edges'),
  getOrgProfile: (id) => request(`/admin/organizations/${id}`),
  getAdminTimeseries: (days = 90) => request(`/admin/timeseries?days=${days}`),
  // Admin list/detail/edit — all through the backend (service role), so every
  // admin view is one consistent source of truth (no direct client Supabase).
  adminListOrgs: (includeDeleted = false) =>
    request(`/admin/organizations${includeDeleted ? '?include_deleted=true' : ''}`),
  adminUpdateOrg: (id, patch) => request(`/admin/organizations/${id}`, { method: 'PATCH', body: patch }),
  adminListUsers: () => request('/admin/users'),
  adminGetUser: (id) => request(`/admin/users/${id}`),
  recordLogin: () => request('/activity/login', { method: 'POST' }),
  adminUpdateUser: (id, patch) => request(`/admin/users/${id}`, { method: 'PATCH', body: patch }),
  // Referral links (in-house attribution — see src/lib/referral.js)
  // Downloads a CSV of every organization (rows = 'orgs' | 'members' | 'locations').
  // Customer data exports (ZIP): one patient's complete record, or the whole practice.
  downloadExport: async (orgId, path, fallbackName) => {
    const headers = await getHeaders(orgId);
    delete headers['Content-Type'];
    const res = await fetch(`${API_URL}${path}`, { headers });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Export failed');
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const name = (cd.match(/filename="([^"]+)"/) || [])[1] || fallbackName;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return name;
  },
  exportPatientRecord: (orgId, clientId) => api.downloadExport(orgId, `/exports/patient/${clientId}`, 'patient-record.zip'),
  exportPracticeData: (orgId) => api.downloadExport(orgId, '/exports/practice', 'practice-export.zip'),
  adminExportOrganizationsCsv: async (rows = 'orgs') => {
    const headers = await getHeaders();
    delete headers['Content-Type'];
    const res = await fetch(`${API_URL}/admin/organizations-export.csv?rows=${rows}`, { headers });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Export failed');
    const blob = await res.blob();
    const name = (res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || `medilink-${rows}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },
  adminListReferrals: () => request('/admin/referrals'),
  adminReferralLinks: () => request('/admin/referral-links'),
  adminListCaseReferrals: () => request('/admin/case-referrals'),
  adminRenotifyReferral: (id) => request(`/admin/case-referrals/${id}/renotify`, { method: 'POST' }),
  adminDeleteReferral: (id) => request(`/admin/case-referrals/${id}`, { method: 'DELETE' }),
  adminDeleteReferrals: (ids) => request('/admin/case-referrals/delete', { method: 'POST', body: { ids } }),
  // Admin sends a referral on a firm's behalf; it lands in both Referrals pages like any other
  adminReferralParties: () => request('/admin/referral-parties'),
  adminOrgClients: (orgId) => request(`/admin/organizations/${orgId}/clients`),
  adminCreateCaseReferral: (body) => request('/admin/case-referrals', { method: 'POST', body }),
  // Give a practice its free trial back (or extend a running one)
  adminRestartTrial: (orgId, days) => request(`/admin/organizations/${orgId}/restart-trial`, { method: 'POST', body: { days } }),
  adminCreateReferral: (body) => request('/admin/referrals', { method: 'POST', body }),
  adminUpdateReferral: (id, patch) => request(`/admin/referrals/${id}`, { method: 'PATCH', body: patch }),

  // Client profiles + custom fields
  getClientProfile:    (orgId, userId)         => request(`/contacts/${userId}/profile`, { orgId }),
  updateClientProfile: (orgId, userId, data)   => request(`/contacts/${userId}/profile`, { method: 'PATCH', body: data, orgId }),
  getClientDocuments:  (orgId, userId)         => request(`/contacts/${userId}/documents`, { orgId }),
  listCustomFields:    (orgId)                 => request('/custom-fields', { orgId }),
  createCustomField:   (orgId, data)           => request('/custom-fields', { method: 'POST', body: data, orgId }),
  updateCustomField:   (orgId, id, data)       => request(`/custom-fields/${id}`, { method: 'PATCH', body: data, orgId }),
  deleteCustomField:   (orgId, id)             => request(`/custom-fields/${id}`, { method: 'DELETE', orgId }),
  reorderCustomFields: (orgId, items)          => request('/custom-fields/reorder', { method: 'POST', body: items, orgId }),

  // Stripe billing — checkout-session returns { client_secret } for
  // EmbeddedCheckout (no redirect). Portal session still returns a URL.
  getSubscription: (orgId) => request('/stripe/subscription', { orgId }),
  createCheckoutSession: (orgId) => request('/stripe/checkout-session', { method: 'POST', orgId }),
  createPortalSession:   (orgId, body = {}) => request('/stripe/portal-session',   { method: 'POST', body, orgId }),
  reactivateSubscription: (orgId) => request('/stripe/reactivate', { method: 'POST', orgId }),
  cancelSubscription:    (orgId) => request('/stripe/cancel-subscription', { method: 'POST', orgId }),
  resumeSubscription:    (orgId) => request('/stripe/resume-subscription', { method: 'POST', orgId }),

  // New-org signup: org is materialized only after Stripe checkout succeeds.
  // No orgId on either call — the user hasn't joined an org yet.
  createNewOrgCheckoutSession: (data)        => request('/signup/checkout', { method: 'POST', body: data }),
  finalizeNewOrgSignup:        (session_id) => request('/signup/complete', { method: 'POST', body: { session_id } }),
  // Second signal for the SignupCompletePage poll — looks for any
  // non-deleted membership for the signed-in user. As soon as the org+
  // member row lands (via webhook OR finalize endpoint, whichever wins),
  // this flips and we can advance the user into the app.
  getMyMemberships:            ()           => request('/signup/my-memberships'),
  // Registration progress lives on the user row so a return visit (any device) resumes.
  getSignupResume:             ()           => request('/signup/resume'),
  saveSignupDraft:             (draft)      => request('/signup/draft', { method: 'PUT', body: draft }),

  // Spot-verify an NPI against CMS NPPES at signup time. Returns
  // { ok: true, record } on success, throws on the 400/500 with the
  // server-provided reason in the error message.
  verifyNpi: (npi) => request('/signup/verify-npi', { method: 'POST', body: { npi } }),

  // Stripe Identity (gov't ID + selfie) for the signup user. Returns
  // { client_secret } that gets fed into stripe.verifyIdentity().
  createIdentitySession: ()      => request('/signup/identity-session', { method: 'POST' }),
  // Poll after the Stripe modal closes — webhook is the source of truth.
  getIdentityStatus:     ()      => request('/signup/identity-status',  { method: 'POST' }),
  // Update users.first_name / last_name to match the verified ID so the
  // mismatched-name gate passes.
  updateMyName:          (body)  => request('/signup/me-name',          { method: 'PATCH', body }),
  // Law firm org creation — gated server-side on Stripe Identity
  // verification + name match, then calls create_organization_with_owner
  // via a user-bound client. Body: { name, address_line1, city, state,
  // zip, phone }. Returns { ok, org_id }.
  createLawFirm:         (body)  => request('/signup/create-law-firm',  { method: 'POST', body }),

  // Business Associate Agreement — electronic execution. Not behind the
  // subscription gate: an org that has not signed still needs to sign.
  baaStatus:             (orgId) => request('/baa/status',   { orgId }),
  baaDocument:           (orgId) => request('/baa/document', { orgId }),
  baaAccept:             (orgId, body) => request('/baa/accept', { method: 'POST', body, orgId }),

  // Platform admin: BAA execution status across all orgs, and recording an
  // execution that happened off-platform (signed PDF, or the customer's own
  // BAA form) so the in-app gate stops asking them.
  adminBaaStatus:        ()      => request('/admin/baa'),
  adminRecordPaperBaa:   (orgId, body) => request(`/admin/baa/${orgId}/paper`, { method: 'POST', body }),

  // Native form builder (custom forms)
  listCustomForms:        (orgId) => request('/custom-forms', { orgId }),
  createCustomForm:       (orgId, data) => request('/custom-forms', { method: 'POST', body: data, orgId }),
  updateCustomForm:       (orgId, id, data) => request(`/custom-forms/${id}`, { method: 'PATCH', body: data, orgId }),
  deleteCustomForm:       (orgId, id) => request(`/custom-forms/${id}`, { method: 'DELETE', orgId }),
  getCustomFormResponses: (orgId, id) => request(`/custom-forms/${id}/responses`, { orgId }),
  getMyCustomForms:       (orgId) => request('/custom-forms/my', { orgId }),
  submitCustomForm:       (orgId, id, answers) => request(`/custom-forms/${id}/responses`, { method: 'POST', body: { answers }, orgId }),

  // Demo-only Message Center (backend 404s these outside the demo env)
  demoOutbox:      () => request('/demo/outbox'),
  demoOutboxClear: () => request('/demo/outbox', { method: 'DELETE' }),

  // Documents — generic file upload/download + cross-org sharing
  listDocuments:        (orgId) => request('/documents', { orgId }),
  listSharedWithMe:     (orgId) => request('/documents/shared-with-me', { orgId }),
  getDocumentDownloadUrl: (orgId, id) => request(`/documents/${id}/download`, { orgId }),
  shareDocument:        (orgId, id, data) => request(`/documents/${id}/share`, { method: 'POST', body: data, orgId }),
  revokeDocumentShare:  (orgId, shareId) => request(`/documents/shares/${shareId}`, { method: 'DELETE', orgId }),
  deleteDocument:       (orgId, id) => request(`/documents/${id}`, { method: 'DELETE', orgId }),
  uploadDocument:       async (orgId, file, description, clientId) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    if (clientId) formData.append('client_id', clientId);
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    const res = await fetch(`${API_URL}/documents`, {
      method: 'POST',
      headers: { Authorization: token ? `Bearer ${token}` : '', 'X-Org-Id': orgId },
      body: formData,
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Upload failed');
    return result;
  },
};
